import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientAPI } from '../services/api';

// Add validation helper functions
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidPhoneNumber = (phone) => {
  // Accept any 10-digit number
  const phoneRegex = /^\d{10}$/;
  return phoneRegex.test(phone.replace(/[\s-]/g, ''));
};

const Diagnosis = ({ onUploadSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Profile Details
    firstName: '',
    surname: '',
    email: '',
    phone: '',
    gender: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postcode: '',
    country: '',
    dob: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    preferredSessionType: 'video',
    countryCode: '',
    
    // Step 2: ADHD Diagnosis
    diagnosisDate: '',
    diagnosedBy: '',
    clinicName: '',
    adhdType: '',
    diagnosisDocument: null,
    
    // Step 3: Current Medications
    adhdMedicationName: '',
    dosage: '',
    frequency: '',
    prescribingDoctor: '',
    doctorContact: '',
    lastPrescriptionDate: '',
    prescriptionDocument: null,
    otherMedications: '',
    allergies: '',
    otherConditions: '',
    
    // Step 4: GP Info
    gpFullName: '',
    gpClinicName: '',
    gpPhoneNumber: '',
    lastGpVisit: '',
    
    // Step 5: Assessment
    adhdSymptomsManaged: 50,
    workPerformance: 50,
    dailyLearning: 50,
    sleepQuality: 50,
    overallMood: 50,
    qualityOfLife: 50,
    areasNeedHelp: '',
    
    // Step 6: Preferences
    sessionType: 'video',
    preferredTimes: [],
    timezone: 'Australia/Sydney'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showSuccessTooltip, setShowSuccessTooltip] = useState(false);
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState({
    doctorContact: '',
    gpPhoneNumber: '',
    email: '',
    phone: ''
  });
  const [patientProfileId, setPatientProfileId] = useState(null);
  const [existingDiagnosis, setExistingDiagnosis] = useState(null);
  const [patientProfile, setPatientProfile] = useState(null);

  // Calculate profile completion percentage
  const calculateProfileCompletion = () => {
    const requiredFields = ['firstName', 'surname', 'email', 'phone', 'gender', 'dob', 'emergencyContactName', 'emergencyContactPhone'];
    const optionalFields = ['addressLine1', 'city', 'state', 'postcode', 'countryCode'];
    
    let completed = 0;
    let total = requiredFields.length + optionalFields.length;
    
    // Check required fields
    requiredFields.forEach(field => {
      if (formData[field] && formData[field].toString().trim()) {
        completed++;
      }
    });
    
    // Check optional fields
    optionalFields.forEach(field => {
      if (formData[field] && formData[field].toString().trim()) {
        completed++;
      }
    });
    
    return Math.round((completed / total) * 100);
  };

  // Get user ID from localStorage
  const getUser = () => {
    const userString = localStorage.getItem('user');
    return userString ? JSON.parse(userString) : null;
  };

  // Single useEffect to handle all initialization
  useEffect(() => {
    const initializeComponent = async () => {
      // Check authentication first
      const accessToken = localStorage.getItem('access_token');
      const user = getUser();
      
      if (!accessToken || !user || !user.id) {
        navigate('/login');
        return;
      }

      try {
        // Get patient profile and check diagnosis
        const profileResponse = await patientAPI.getMyProfile();
        if (profileResponse?.patientProfile?._id) {
          const patientId = profileResponse.patientProfile._id;
          setPatientProfileId(patientId);
          setPatientProfile(profileResponse.patientProfile);
          
          // Map existing profile data to form
          const profile = profileResponse.patientProfile;
          setFormData(prev => ({
            ...prev,
            firstName: profile.name ? profile.name.split(' ')[0] || '' : '',
            surname: profile.name ? profile.name.split(' ').slice(1).join(' ') || '' : '',
            email: profile.email || '',
            phone: profile.phone ? profile.phone.replace(/^\+91/, '') : '',
            dob: profile.dateOfBirth ? profile.dateOfBirth.split('T')[0] : '',
            emergencyContactName: profile.emergencyContact?.name || '',
            emergencyContactPhone: profile.emergencyContact?.phone || '',
            preferredSessionType: profile.preferred_session_type || 'video',
            countryCode: profile.countryCode || ''
          }));
          
          try {
            const diagnosisResponse = await patientAPI.getDiagnosisHistories(patientId);
            // If diagnosisResponse is null, it means no diagnosis found
            if (diagnosisResponse && diagnosisResponse._id) {
              setExistingDiagnosis(diagnosisResponse);
            } else {
              setExistingDiagnosis(null);
            }
          } catch (diagnosisError) {
            console.error('Error checking diagnosis:', diagnosisError);
            setError('Failed to check existing diagnosis. Please try again.');
          }
        }
      } catch (error) {
        console.error('Error in initialization:', error);
        setError('Failed to load profile. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    initializeComponent();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (existingDiagnosis && existingDiagnosis._id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="mb-6">
              <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Diagnosis Information Already Submitted
            </h2>
            <p className="text-gray-600 mb-6">
              You have already provided your diagnosis information. This helps us provide you with better care and personalized support.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get today's date in YYYY-MM-DD format for max date restriction
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Transform form data to match backend model
  const transformFormData = () => {
    const user = getUser();
    console.log('Current user:', user);
    
    if (!user || !user.id) {
      throw new Error('User not found. Please log in again.');
    }

    // If we have a patient profile ID, use it, otherwise use the user ID
    const patient_id = patientProfileId || user.id;
    console.log('Using ID for diagnosis:', patient_id);

    // Convert slider values from 0-100 to 1-10 scale
    const convertSliderValue = (value) => Math.max(1, Math.min(10, Math.round((value / 100) * 10)));

    // Convert frequency values to match backend enum
    const frequencyMap = {
      'daily': 'Once daily',
      'twice-daily': 'Twice daily',
      'three-times-daily': 'Three times daily',
      'as-needed': 'As needed',
      'weekly': 'As needed',
      'other': 'As needed'
    };

    // Convert ADHD type to lowercase to match backend enum
    const adhdTypeMap = {
      'Inattentive': 'inattentive',
      'Hyperactive': 'hyperactive',
      'Combined': 'combined',
      'Unknown': 'unknown'
    };

    return {
      patient_id,
      // Profile Information
      profile: {
        firstName: formData.firstName,
        surname: formData.surname,
        fullName: `${formData.firstName} ${formData.surname}`.trim(),
        email: formData.email,
        phone: formData.phone,
        gender: formData.gender,
        address: {
          line1: formData.addressLine1,
          line2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          postcode: formData.postcode,
          country: formData.country
        },
        dateOfBirth: formData.dob,
        emergencyContact: {
          name: formData.emergencyContactName,
          phone: formData.emergencyContactPhone
        },
        preferredSessionType: formData.preferredSessionType,
        countryCode: formData.countryCode
      },
      diagnosis_date: formData.diagnosisDate,
      diagnosing_doctor_name: formData.diagnosedBy,
      diagnosing_clinic_name: formData.clinicName,
      adhd_type: adhdTypeMap[formData.adhdType] || 'unknown',
      diagnosis_document_url: formData.diagnosisDocument ? formData.diagnosisDocument.name : null,

      // Medication Info
      medication_name: formData.adhdMedicationName,
      dosage: formData.dosage,
      frequency: frequencyMap[formData.frequency] || 'Once daily',
      prescribing_doctor_name: formData.prescribingDoctor,
      prescribing_doctor_contact: formData.doctorContact,
      last_prescription_date: formData.lastPrescriptionDate,
      prescription_document_url: formData.prescriptionDocument ? formData.prescriptionDocument.name : null,
      other_medications: formData.otherMedications ? formData.otherMedications.split('\n').filter(item => item.trim()) : [],
      known_allergies: formData.allergies ? formData.allergies.split('\n').filter(item => item.trim()) : [],
      medical_conditions: formData.otherConditions ? formData.otherConditions.split('\n').filter(item => item.trim()) : [],

      // GP Info
      gp_name: formData.gpFullName,
      gp_clinic: formData.gpClinicName,
      gp_phone: formData.gpPhoneNumber,
      last_visit_date: formData.lastGpVisit,

      // Functional Assessment (convert from 0-100 to 1-10 scale)
      symptom_management: convertSliderValue(formData.adhdSymptomsManaged),
      work_impact: convertSliderValue(formData.workPerformance),
      study_impact: convertSliderValue(formData.dailyLearning),
      sleep_quality: convertSliderValue(formData.sleepQuality),
      mood: convertSliderValue(formData.overallMood),
      lifestyle: convertSliderValue(formData.qualityOfLife),
      help_needed: formData.areasNeedHelp,

      // Preferences
      session_preference: formData.sessionType,
      preferred_appointment_times: formData.preferredTimes,
      timezone: formData.timezone
    };
  };

  // Handle file upload to server (placeholder - not implemented in backend)
  const uploadFile = async (file, type) => {
    if (!file) return null;
    
    // For now, just return the filename as a placeholder
    // In a real implementation, you would upload to a file storage service
    console.log(`File upload placeholder: ${file.name} (${type})`);
    return `/uploads/${type}/${file.name}`;
  };

  const steps = [
    { label: "Profile Details", key: "profile_details" },
    { label: "ADHD Diagnosis", key: "diagnosis" },
    { label: "Medications", key: "medications" },
    { label: "GP Info", key: "gpInfo" },
    { label: "Assessment", key: "assessment" },
    { label: "Preferences", key: "preferences" }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation errors when user starts typing
    if (name === 'doctorContact' || name === 'gpPhoneNumber' || name === 'email' || name === 'phone') {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Validate doctor contact (email or phone)
    if (name === 'doctorContact') {
      if (value && !isValidEmail(value) && !isValidPhoneNumber(value)) {
        setValidationErrors(prev => ({
          ...prev,
          doctorContact: 'Please enter a valid email or phone number'
        }));
      }
    }

    // Validate GP phone number
    if (name === 'gpPhoneNumber') {
      if (value && !isValidPhoneNumber(value)) {
        setValidationErrors(prev => ({
          ...prev,
          gpPhoneNumber: 'Please enter a valid  phone number'
        }));
      }
    }

    // Validate email
    if (name === 'email') {
      if (value && !isValidEmail(value)) {
        setValidationErrors(prev => ({
          ...prev,
          email: 'Please enter a valid email address'
        }));
      }
    }

    // Validate phone number
    if (name === 'phone') {
      if (value && !isValidPhoneNumber(value)) {
        setValidationErrors(prev => ({
          ...prev,
          phone: 'Please enter a valid 10-digit phone number'
        }));
      }
    }
  };

  const handleSliderChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseInt(value)
    }));
  };

  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        [fieldName]: file
      }));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, fieldName) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.type === 'application/pdf' || file.type.startsWith('image/'))) {
      setFormData(prev => ({
        ...prev,
        [fieldName]: file
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === 'preferredTimes') {
      setFormData(prev => ({
        ...prev,
        preferredTimes: checked 
          ? [...prev.preferredTimes, value]
          : prev.preferredTimes.filter(time => time !== value)
      }));
    }
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return formData.firstName && formData.surname && formData.email && formData.phone && formData.gender && formData.dob && 
               !validationErrors.email && !validationErrors.phone;
      case 2:
        return formData.diagnosisDate && formData.diagnosedBy && formData.adhdType;
      case 3:
        return formData.adhdMedicationName && formData.dosage && formData.frequency && 
               formData.prescribingDoctor && formData.doctorContact && formData.lastPrescriptionDate &&
               !validationErrors.doctorContact; // Add validation check
      case 4:
        return formData.gpFullName && formData.gpClinicName && formData.gpPhoneNumber &&
               !validationErrors.gpPhoneNumber; // Add validation check
      case 5:
        return formData.adhdSymptomsManaged !== undefined && formData.workPerformance !== undefined && 
               formData.dailyLearning !== undefined && formData.sleepQuality !== undefined && 
               formData.overallMood !== undefined && formData.qualityOfLife !== undefined;
      case 6:
        return formData.sessionType && formData.timezone;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const getTodayMonth = () => {
      const today = new Date();
      return today.toISOString().slice(0, 7);
    };

  const handleSubmit = async () => {
    if (validateCurrentStep()) {
      setLoading(true);
      setError(null);
      
      try {
        // First update the patient profile if we have one
        if (patientProfile && patientProfileId) {
          try {
            const profileUpdateData = {
              name: `${formData.firstName} ${formData.surname}`.trim(),
              email: formData.email,
              phone: formData.phone,
              dateOfBirth: formData.dob,
              emergencyContact: {
                name: formData.emergencyContactName,
                phone: formData.emergencyContactPhone
              },
              preferred_session_type: formData.preferredSessionType,
              countryCode: formData.countryCode
            };
            
            // Update profile (you'll need to implement this API endpoint)
            // await patientAPI.updateProfile(patientProfileId, profileUpdateData);
            console.log('Profile update data:', profileUpdateData);
          } catch (profileError) {
            console.error('Profile update error:', profileError);
            // Continue with diagnosis submission even if profile update fails
          }
        }
        
        // Upload files first if they exist
        let diagnosisDocumentUrl = null;
        let prescriptionDocumentUrl = null;
        
        if (formData.diagnosisDocument) {
          diagnosisDocumentUrl = await uploadFile(formData.diagnosisDocument, 'diagnosis');
        }
        
        if (formData.prescriptionDocument) {
          prescriptionDocumentUrl = await uploadFile(formData.prescriptionDocument, 'prescription');
        }
        
        // Transform form data and include document URLs
        const transformedData = transformFormData();
        if (diagnosisDocumentUrl) {
          transformedData.diagnosis_document_url = diagnosisDocumentUrl;
        }
        if (prescriptionDocumentUrl) {
          transformedData.prescription_document_url = prescriptionDocumentUrl;
        }
        
        console.log('Submitting diagnosis data:', transformedData);
        
        // Call the diagnosis history API
        const result = await patientAPI.diagnosisHistory(transformedData);
        console.log('Diagnosis submitted successfully:', result);

        // Show success tooltip
        setShowSuccessTooltip(true);

        // Call the success callback if provided
        if (onUploadSuccess) {
          onUploadSuccess(result);
        }

        setSuccess(true);
        setError(null);
        
        // Hide tooltip after 3 seconds and navigate to doctors page
        setTimeout(() => {
          setShowSuccessTooltip(false);
          // Navigate to nurses list for consultation booking
          navigate('/nurses/list');
        }, 3000);

      } catch (err) {
        console.error('Submit error:', err);
        setError(err.response?.data?.error || err.message || 'Failed to submit form. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
  
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Information</h2>
              <p className="text-gray-600">
                {patientProfile ? 'Review and update your profile information' : 'Please provide your basic details'}
              </p>   
            </div>

                  {/* Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="John"
                />
                {patientProfile && formData.firstName && (
                  <p className="mt-1 text-xs text-green-600">✓ Pre-filled from your profile</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Surname <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="surname"
                  value={formData.surname}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Doe"
                />
                {patientProfile && formData.surname && (
                  <p className="mt-1 text-xs text-green-600">✓ Pre-filled from your profile</p>
                )}
              </div>
            </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            validationErrors.email ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="john.doe@example.com"
        />
        {validationErrors.email && (
          <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
        )}
        {patientProfile && formData.email && (
          <p className="mt-1 text-xs text-green-600">✓ Pre-filled from your profile</p>
        )}
      </div>

      {/* Phone */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Phone number <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
          inputMode="tel"
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            validationErrors.phone ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="0xxx xxx xxx"
        />
        {validationErrors.phone && (
          <p className="mt-1 text-sm text-red-600">{validationErrors.phone}</p>
        )}
        {patientProfile && formData.phone && (
          <p className="mt-1 text-xs text-green-600">✓ Pre-filled from your profile</p>
        )}
      </div>

      {/* Gender */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Gender <span className="text-red-500">*</span>
        </label>
        <div className="space-y-3">
          
          {["Male", "Female", "Other"].map((g) => (
            <label key={g} className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="gender"
                value={g}
                checked={formData.gender === g}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-gray-700">{g}</span>
            </label>
          ))}

        </div>
      </div>

      {/* Address */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Full home address
        </label>
        <div className="space-y-4">
          <input
            type="text"
            name="addressLine1"
            value={formData.addressLine1}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Address line 1 *"
          />
          <input
            type="text"
            name="addressLine2"
            value={formData.addressLine2}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Address line 2 (optional)"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="City *"
            />
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="State *"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              name="postcode"
              value={formData.postcode}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Post Code *"
            />
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Country (optional)"
            />
          </div>
        </div>
      </div>

      {/* DOB */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Date of Birth <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="date"
            name="dob"
            value={formData.dob}
            onChange={handleInputChange}
            max={getTodayDate()}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
            placeholder="YYYY / MM / DD"
            onClick={(e) => e.target.showPicker && e.target.showPicker()}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">Click the input field to open the calendar</p>
      </div>
    </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">ADHD Diagnosis History</h2>
              <p className="text-gray-600">Please provide information about your ADHD diagnosis</p>
            </div>

            {/* Diagnosis Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                When were you officially diagnosed with ADHD? <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="month"
                  name="diagnosisDate"
                  value={formData.diagnosisDate}
                  onChange={handleInputChange}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  max={getTodayMonth()}  
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                />

              </div>
              <p className="text-xs text-gray-500 mt-1">Click the input field to open the calendar</p>
            </div>

            {/* Diagnosed By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Who diagnosed you? <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="diagnosedBy"
                value={formData.diagnosedBy}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Dr. Smith"
              />
            </div>

            {/* Clinic Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clinic name <span className="text-gray-500">(optional)</span>
              </label>
              <input
                type="text"
                name="clinicName"
                value={formData.clinicName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Melbourne Medical Centre"
              />
            </div>

            {/* ADHD Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                What type of ADHD were you diagnosed with? <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                {['Inattentive', 'Hyperactive', 'Combined', 'Unknown'].map((type) => (
                  <label key={type} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="adhdType"
                      value={type}
                      checked={formData.adhdType === type}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Document Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Upload your diagnosis document <span className="text-gray-500">(optional)</span>
              </label>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  formData.diagnosisDocument 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, 'diagnosisDocument')}
              >
                <div className="flex flex-col items-center space-y-4">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <div>
                    <p className="text-gray-600">Drag and drop PDF, JPG, or PNG to upload</p>
                    <p className="text-gray-500 text-sm mt-1">or</p>
                  </div>
                  <label className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
                    Browse files
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileUpload(e, 'diagnosisDocument')}
                      className="hidden"
                    />
                  </label>
                  {formData.diagnosisDocument && (
                    <p className="text-sm text-blue-600 mt-2">
                      ✓ {formData.diagnosisDocument.name}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Current Medications</h2>
              <p className="text-gray-600">Step 2 of 5</p>
            </div>

            {/* Current ADHD Medication Section */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Current ADHD Medication</h3>
              
              {/* ADHD Medication Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ADHD Medication Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="adhdMedicationName"
                  value={formData.adhdMedicationName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ritalin, Concerta, Vyvanse, etc."
                />
              </div>

              {/* Dosage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dosage <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="dosage"
                  value={formData.dosage}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 20mg"
                />
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How often do you take it <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    name="frequency"
                    value={formData.frequency}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                  >
                    <option value="">Select frequency</option>
                    <option value="daily">Daily</option>
                    <option value="twice-daily">Twice daily</option>
                    <option value="three-times-daily">Three times daily</option>
                    <option value="as-needed">As needed</option>
                    <option value="weekly">Weekly</option>
                    <option value="other">Other</option>
                  </select>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Doctor Information - Two Column Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prescribing/Doctor Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="prescribingDoctor"
                    value={formData.prescribingDoctor}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Dr. Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Doctor Contact (Phone or Email) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="doctorContact"
                    value={formData.doctorContact}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      validationErrors.doctorContact ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter email or phone number"
                  />
                  {validationErrors.doctorContact ? (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.doctorContact}</p>
                  ) : (
                    <p className="mt-1 text-sm text-gray-500">
                      Enter either a valid email (e.g., doctor@clinic.com) or 10-digit phone number (e.g., 0xxx xxx xxx)
                    </p>
                  )}
                </div>
              </div>

              {/* Last Prescription Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  When was your last prescription <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="month"
                    name="lastPrescriptionDate"
                    value={formData.lastPrescriptionDate}
                    onChange={handleInputChange}
                    max={getTodayMonth()}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                    placeholder="Select date"
                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  />
                </div>
              </div>

              {/* Prescription Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Upload your last prescription <span className="text-gray-500">(optional)</span>
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    formData.prescriptionDocument 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, 'prescriptionDocument')}
                >
                  <div className="flex flex-col items-center space-y-4">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <div>
                      <p className="text-gray-600">Drag and drop your prescription here, or click to browse</p>
                      <p className="text-gray-500 text-sm mt-1">Supported formats: PDF, JPG, PNG (Max size: 2MB)</p>
                    </div>
                    <label className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
                      Browse files
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload(e, 'prescriptionDocument')}
                        className="hidden"
                      />
                    </label>
                    {formData.prescriptionDocument && (
                      <p className="text-sm text-blue-600 mt-2">
                        ✓ {formData.prescriptionDocument.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Other Medical Information Section */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Other Medical Information</h3>
              
              {/* Other Medications */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Other medications you're taking <span className="text-gray-500">(optional)</span>
                </label>
                <textarea
                  name="otherMedications"
                  value={formData.otherMedications}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="List any other medications..."
                />
              </div>

              {/* Allergies */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Do you have any known allergies? <span className="text-gray-500">(optional)</span>
                </label>
                <textarea
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="List any allergies..."
                />
              </div>

              {/* Other Conditions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Other diagnoses/conditions <span className="text-gray-500">(optional)</span>
                </label>
                <textarea
                  name="otherConditions"
                  value={formData.otherConditions}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., diabetes, anxiety..."
                />
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Your GP Details</h2>
              <p className="text-gray-600">Please provide information about your regular General Practitioner</p>
            </div>

            {/* GP Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                GP Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="gpFullName"
                value={formData.gpFullName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Dr. Emily Brown"
              />
            </div>

            {/* GP Clinic Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                GP Clinic Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="gpClinicName"
                value={formData.gpClinicName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="City Health Clinic"
              />
            </div>

            {/* GP Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                GP Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="gpPhoneNumber"
                value={formData.gpPhoneNumber}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.gpPhoneNumber ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="0xxx xxx xxx"
              />
              {validationErrors.gpPhoneNumber ? (
                <p className="mt-1 text-sm text-red-600">{validationErrors.gpPhoneNumber}</p>
              ) : (
                <p className="mt-1 text-sm text-gray-500">
                  Enter a 10-digit phone number (e.g., 0xxx xxx xxx)
                </p>
              )}
            </div>

            {/* Last GP Visit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                When did you last visit your GP? <span className="text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <input
                  type="month"
                  name="lastGpVisit"
                  value={formData.lastGpVisit}
                  onChange={handleInputChange}
                  max={getTodayMonth()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                  placeholder="Select date"
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                />
              </div>
            </div>

          </div>
        );
      case 5:
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">How Are You Doing Lately?</h2>
              <p className="text-gray-600">Please rate your current status in those key areas</p>
            </div>

            {/* Assessment Sliders */}
            <div className="space-y-8">
              {/* ADHD Symptoms Management */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  How well are your ADHD symptoms currently managed? <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    name="adhdSymptomsManaged"
                    min="0"
                    max="100"
                    value={formData.adhdSymptomsManaged}
                    onChange={handleSliderChange}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${formData.adhdSymptomsManaged}%, #e5e7eb ${formData.adhdSymptomsManaged}%, #e5e7eb 100%)`
                    }}
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                </div>
              </div>

              {/* Work Performance */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  How much do ADHD symptoms affect your work performance? <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    name="workPerformance"
                    min="0"
                    max="100"
                    value={formData.workPerformance}
                    onChange={handleSliderChange}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${formData.workPerformance}%, #e5e7eb ${formData.workPerformance}%, #e5e7eb 100%)`
                    }}
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                </div>
              </div>

              {/* Daily Learning */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  How much do symptoms impact your daily or learning? <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    name="dailyLearning"
                    min="0"
                    max="100"
                    value={formData.dailyLearning}
                    onChange={handleSliderChange}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${formData.dailyLearning}%, #e5e7eb ${formData.dailyLearning}%, #e5e7eb 100%)`
                    }}
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                </div>
              </div>

              {/* Sleep Quality */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  How would you rate your sleep quality? <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    name="sleepQuality"
                    min="0"
                    max="100"
                    value={formData.sleepQuality}
                    onChange={handleSliderChange}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${formData.sleepQuality}%, #e5e7eb ${formData.sleepQuality}%, #e5e7eb 100%)`
                    }}
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                </div>
              </div>

              {/* Overall Mood */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  How would you rate your overall mood? <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    name="overallMood"
                    min="0"
                    max="100"
                    value={formData.overallMood}
                    onChange={handleSliderChange}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${formData.overallMood}%, #e5e7eb ${formData.overallMood}%, #e5e7eb 100%)`
                    }}
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                </div>
              </div>

              {/* Quality of Life */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  How would you rate your overall quality of life? <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    name="qualityOfLife"
                    min="0"
                    max="100"
                    value={formData.qualityOfLife}
                    onChange={handleSliderChange}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${formData.qualityOfLife}%, #e5e7eb ${formData.qualityOfLife}%, #e5e7eb 100%)`
                    }}
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Areas Need Help */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                What areas do you need help with?
              </label>
              <textarea
                name="areasNeedHelp"
                value={formData.areasNeedHelp}
                onChange={handleInputChange}
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="I want help managing focus and sleep..."
              />
            </div>
          </div>
        );
      case 6:
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Preferences for Ongoing Sessions</h2>
              <p className="text-gray-600">Please set your preferences for monthly sessions</p>
            </div>

            {/* Session Type */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                How would you like to attend your monthly sessions?
              </label>
              <div className="space-y-3">
                <label className="flex items-center space-x-3 cursor-pointer p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <input
                    type="radio"
                    name="sessionType"
                    value="video"
                    checked={formData.sessionType === 'video'}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <div className="flex items-center space-x-3">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span className="text-gray-700">Video call</span>
                  </div>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <input
                    type="radio"
                    name="sessionType"
                    value="phone"
                    checked={formData.sessionType === 'phone'}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <div className="flex items-center space-x-3">
                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className="text-gray-700">Phone call</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Preferred Times */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                What times generally work best for you?
              </label>
              <div className="space-y-3">
                {[
                  { value: 'morning', label: 'Morning (8:00-12:00)' },
                  { value: 'afternoon', label: 'Afternoon (12:00-17:00)' },
                  { value: 'evening', label: 'Evening (17:00-20:00)' }
                ].map((time) => (
                  <label key={time.value} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="preferredTimes"
                      value={time.value}
                      checked={formData.preferredTimes.includes(time.value)}
                      onChange={handleCheckboxChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-gray-700">{time.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Timezone */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Please confirm your time zone
              </label>
              <div className="relative">
                <select
                  name="timezone"z
                  value={formData.timezone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                >

                  <option value="New South Wales">New South Wales</option>
                  <option value="Northern Territory">Northern Territory</option>
                  <option value="Queensland">Queensland</option>
                  <option value="South Australia">South Australia</option> 
                  <option value="Tasmania">Tasmania</option> 
                  <option value="Victoria">Victoria</option> 
                  <option value="Western Australia">Western Australia</option> 

                </select>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2.25A9.75 9.75 0 002.25 12c0 5.385 4.365 9.75 9.75 9.75 5.385 0 9.75-4.365 9.75-9.75 0-5.385-4.365-9.75-9.75-9.75zM12 6a6 6 0 016 6c0 3.314-2.686 6-6 6s-6-2.686-6-6c0-3.314 2.686-6 6-6z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 relative">
      <style jsx>{`
        .slider-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          background: #3b82f6;
          border-radius: 50%;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        .slider-thumb::-moz-range-thumb {
          width: 20px;
          height: 20px;
          background: #3b82f6;
          border-radius: 50%;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
      `}</style>
      <div className="max-w-3xl w-full mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Welcome to Your ADHD Care Journey</h1>
          <p className="text-gray-600">Let's start by completing your diagnosis information to begin your assessment.</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center">
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                      index + 1 === currentStep
                        ? 'bg-blue-600 text-white'
                        : index + 1 < currentStep
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {index + 1 < currentStep ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span
                    className={`text-xs mt-2 font-medium ${
                      index + 1 === currentStep
                        ? 'text-blue-600'
                        : index + 1 < currentStep
                        ? 'text-blue-600'
                        : 'text-gray-500'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-4 transition-colors ${
                      index + 1 < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-8 ${success ? 'opacity-50 pointer-events-none' : ''}`}>
          {renderStepContent()}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-green-800">Diagnosis information submitted successfully! Redirecting to dashboard...</span>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          {currentStep > 1 && (
            <button
              onClick={prevStep}
              className="px-6 py-3 rounded-lg font-medium text-gray-600 hover:text-gray-900 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back to Step {currentStep - 1}
            </button>
          )}
          {currentStep < steps.length ? (
            <button
              onClick={nextStep}
              disabled={!validateCurrentStep() || success}
              className={`ml-auto px-6 py-3 rounded-lg font-medium transition-colors flex items-center ${
                validateCurrentStep() && !success
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Continue to Step {currentStep + 1}
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!validateCurrentStep() || loading || success}
              className={`ml-auto px-6 py-3 rounded-lg font-medium transition-colors flex items-center ${
                validateCurrentStep() && !loading && !success
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                'Submit Diagnosis'
              )}
            </button>
          )}
        </div>
      </div>

      {/* Success Tooltip */}
      {showSuccessTooltip && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg transform transition-all duration-300">
          <div className="flex items-center space-x-2">
            <svg 
              className="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5 13l4 4L19 7" 
              />
            </svg>
            <span className="font-semibold">Diagnosis Completed Successfully!</span>
          </div>
          <p className="text-sm mt-1 opacity-90">Redirecting to nurses list...</p>
        </div>
      )}
    </div>
  );
};

export default Diagnosis; 